import type { BackofficeClient } from "@/api/backoffice-v2/backoffice-client";
import type { SiteId } from "@/types/common/ids";
import type { MaybeRef } from "@vueuse/core";

import { queryOptions, skipToken, useQuery } from "@tanstack/vue-query";
import { isDefined } from "@voltalis/frontend-utils";
import moment from "moment";
import { computed, MaybeRefOrGetter, toRef, toValue } from "vue";

import { expectApiStatuses } from "@/api/api-client-error.ts";
import { autoDiagQueryKeys } from "@/api/backoffice/auto-diag/auto-diag-query-keys.ts";
import BackofficeApi from "@/api/backoffice/backoffice-api.ts";
import { SignatureQueryKeys } from "@/api/backoffice/signature/signature-query-keys.ts";
import { SiteQueryKeys } from "@/api/backoffice/site/site-query-keys.ts";
import { useBackofficeClient } from "@/hooks/backoffice/use-backoffice-client";

const sixMonthsAgo = moment().subtract(6, "months");

export function siteDetailsOptions(
  _siteId: MaybeRefOrGetter<SiteId | undefined>,
  enabled?: MaybeRefOrGetter<boolean>,
  // 'Optional client' is a quirk because it's used in routing code (only to get the queryKey accompanied with the query's result type)
  clientV2?: BackofficeClient,
) {
  const siteId = toRef(_siteId! as SiteId);

  return queryOptions({
    queryKey: SiteQueryKeys.detail2(siteId),
    queryFn: clientV2 ? async () => clientV2.site.getById(siteId.value!, "site-details") : skipToken,
    enabled: computed(() => {
      const enabledValue = isDefined(enabled) ? toValue(enabled) : true;
      return siteId.value !== undefined && enabledValue;
    }),
    meta: { updatesRouteTitle: true },
  });
}

// TODO : number -> Ref<SiteId>
export default function useSiteDetails(_siteId?: MaybeRef<number | undefined>) {
  // The lie here is "ok" because we verify at runtime that siteId is defined (see `enabled` below)
  const siteId = toRef(_siteId! as SiteId);
  const enabled = computed<boolean>(() => siteId.value !== undefined);

  const clientV2 = useBackofficeClient();
  const siteDetailsQuery = useQuery(siteDetailsOptions(siteId, enabled, clientV2));

  const signatureQuery = useQuery({
    queryKey: SignatureQueryKeys.findSignatureDetails(siteId),
    queryFn: () =>
      expectApiStatuses(BackofficeApi.findSiteSignatureDetails({ params: { siteId: siteId.value } }), [404]),
    enabled,
  });

  const siteAutoDiagQuery = useQuery({
    queryKey: computed(() => autoDiagQueryKeys.detail(siteId.value)),
    queryFn: () => BackofficeApi.findAutoDiagBySiteId({ params: { id: siteId.value } }),
    enabled,
  });

  const siteAutoDiagModulatorsQuery = useQuery({
    queryKey: computed(() => autoDiagQueryKeys.modulators(siteId.value)),
    queryFn: () => BackofficeApi.findAutoDiagModulatorsBySiteId({ params: { id: siteId.value } }),
    enabled,
  });

  const siteMomboxQuery = useQuery({
    queryKey: computed(() => SiteQueryKeys.mombox(siteId.value)),
    queryFn: () => BackofficeApi.findSiteMombox({ params: { id: siteId.value } }),
    enabled,
  });

  const siteModulatorsQuery = useQuery({
    queryKey: computed(() => SiteQueryKeys.modulators(siteId.value)),
    queryFn: () => BackofficeApi.findSiteModulators({ params: { id: siteId.value } }),
    enabled,
  });

  const siteAppointmentsQuery = useQuery({
    queryKey: computed(() =>
      SiteQueryKeys.findSiteLastAppointments(siteId.value, moment(sixMonthsAgo).format("YYYY-MM-DD")),
    ),
    queryFn: () =>
      BackofficeApi.findSiteLastAppointments({
        params: { id: siteId.value },
        queries: { from: moment(sixMonthsAgo).format("YYYY-MM-DD") },
      }),
    enabled,
  });

  const siteAppointmentPreparationQuery = useQuery({
    queryKey: SiteQueryKeys.findSiteAppointmentsPreparation(siteId),
    queryFn: () =>
      BackofficeApi.findAppointmentPreparationBySiteId({
        params: { siteId: siteId.value },
      }),
    enabled,
  });

  const siteOffersQuery = useQuery({
    queryKey: SiteQueryKeys.findSiteOffersWithConditions(siteId),
    queryFn: () =>
      expectApiStatuses(
        BackofficeApi.findSiteOffersWithConditionsBySiteId({
          params: { siteId: siteId.value },
        }),
        [404],
      ),
    enabled,
  });

  return {
    siteDetailsQuery,
    siteAutoDiagQuery,
    siteAutoDiagModulatorsQuery,
    signatureQuery,
    siteMomboxQuery,
    siteModulatorsQuery,
    siteAppointmentsQuery,
    siteAppointmentPreparationQuery,
    siteOffersQuery,
  };
}
